import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Percent, Plus, Pencil, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useVendorPromos, useVendorRefresh } from "@/hooks/useVendor";
import { formatDate } from "@/lib/format";
import { AdminModal, Field, Pill, inputCls } from "@/pages/admin/ui";

const EMPTY = { code: "", percent: 10, min_amount: 0, starts_at: "", expires_at: "", is_active: true };

const PROMO_STATUS = {
  active: { tone: "green", label: "🟢 Actif" },
  scheduled: { tone: "amber", label: "🕒 Programmé" },
  expired: { tone: "slate", label: "⛔ Expiré" },
  inactive: { tone: "slate", label: "⚪ Inactif" },
};

export default function VendorPromos() {
  const { data: promos, isLoading } = useVendorPromos();
  const refresh = useVendorRefresh();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const openNew = () => { setForm(EMPTY); setEditId(""); setOpen(true); };
  const openEdit = (p) => { setForm({ code: p.code, percent: p.percent, min_amount: p.min_amount || 0, starts_at: p.starts_at || "", expires_at: p.expires_at || "", is_active: p.is_active }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.code.trim()) { toast.error("Code obligatoire"); return; }
    setSaving(true);
    const payload = { ...form, percent: parseInt(form.percent || 0, 10), min_amount: parseInt(form.min_amount || 0, 10) };
    try {
      if (editId) await api.put(`/vendor/promo-codes/${editId}`, payload);
      else await api.post("/vendor/promo-codes", payload);
      toast.success(editId ? "Code mis à jour" : "Code promo créé — valable uniquement sur vos produits et formations");
      setOpen(false); refresh(["vendor-promos"]);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce code promo ?")) return;
    try { await api.delete(`/vendor/promo-codes/${id}`); toast.success("Supprimé"); refresh(["vendor-promos"]); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="vendor-promos">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Codes promo</h1>
          <p className="mt-1 text-sm text-slate-500">Vos codes ne fonctionnent que sur VOS produits et formations.</p>
        </div>
        <button onClick={openNew} data-testid="promo-add" className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-dark">
          <Plus className="h-4 w-4" /> Créer un code
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : !promos?.length ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
          <Percent className="mx-auto h-9 w-9 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucun code promo. Créez-en un pour booster vos ventes.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="p-4">Code</th><th className="p-4">Réduction</th><th className="p-4">Montant min.</th>
                <th className="p-4">Expiration</th><th className="p-4">Statut</th><th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {promos.map((p) => (
                <tr key={p.id} data-testid={`vpromo-${p.id}`}>
                  <td className="p-4 font-mono font-black text-brand">{p.code}</td>
                  <td className="p-4 font-bold text-ink">−{p.percent}%</td>
                  <td className="p-4 text-slate-600">{p.min_amount ? `${p.min_amount.toLocaleString("fr-FR")} FCFA` : "—"}</td>
                  <td className="p-4 text-xs text-slate-500">{p.starts_at ? `Dès ${formatDate(p.starts_at)}` : "Immédiat"}{p.expires_at ? ` → ${formatDate(p.expires_at)}` : ""}</td>
                  <td className="p-4">{(() => { const s = PROMO_STATUS[p.schedule_status] || PROMO_STATUS[p.is_active ? "active" : "inactive"]; return <Pill tone={s.tone}>{s.label}</Pill>; })()}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => openEdit(p)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:text-brand"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(p.id)} data-testid={`vpromo-del-${p.id}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminModal open={open} onOpenChange={setOpen} title={editId ? "Modifier le code promo" : "Nouveau code promo"}>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Code"><input value={form.code} onChange={set("code")} className={`${inputCls} uppercase`} placeholder="MONCODE10" data-testid="promo-code" /></Field>
            <Field label="Réduction (%)"><input type="number" min="1" max="90" value={form.percent} onChange={set("percent")} className={inputCls} data-testid="promo-percent" /></Field>
            <Field label="Montant minimum (FCFA)"><input type="number" min="0" value={form.min_amount} onChange={set("min_amount")} className={inputCls} /></Field>
            <Field label="Début (optionnel)"><input type="date" value={form.starts_at ? form.starts_at.slice(0, 10) : ""} onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value ? new Date(e.target.value).toISOString() : "" }))} className={inputCls} data-testid="promo-starts" /></Field>
            <Field label="Expire le (optionnel)"><input type="date" value={form.expires_at ? form.expires_at.slice(0, 10) : ""} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" }))} className={inputCls} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm font-bold text-ink">
            <input type="checkbox" checked={form.is_active} onChange={set("is_active")} className="h-4 w-4 accent-brand" data-testid="promo-active" /> Code actif
          </label>
          <button onClick={save} disabled={saving} data-testid="promo-save" className="w-full rounded-full bg-brand py-3 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Enregistrer"}
          </button>
        </div>
      </AdminModal>
    </div>
  );
}
